import React, { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Campaign } from 'apis/restaurant-api/merchant-admin/campaigns/types';
import MonetaryAmount from 'common/formatting/MonetaryAmount';
import { usePluralization } from 'common/hooks';
import { Typography } from 'common/typography';
import { useCampaignModuleContext } from 'modules/campaigns/context';
import { UsageType } from 'modules/campaigns/types';

import CampaignFormItem from '../CampaignFormItem';

export type RulesDetailsProps = {
  minBasketSize?: NonNullable<Campaign['min_basket_size']>;
  firstPurchaseOnly: Campaign['first_purchase_only'];
  usageType: UsageType;
  perUserUsageLimit?: Campaign['per_user_usage_limit'];
  action?: ReactNode;
  'data-testid'?: string;
};

const CampaignRulesViewText = styled(Typography.Body3)`
  display: block;
`;

const RulesDetails = ({
  minBasketSize,
  firstPurchaseOnly,
  usageType,
  perUserUsageLimit,
  action,
  ...props
}: RulesDetailsProps) => {
  const { FormattedPluralMessage } = usePluralization();
  const withBasketSize = minBasketSize !== undefined;
  const { currency } = useCampaignModuleContext();

  return (
    <CampaignFormItem
      data-testid={props['data-testid']}
      action={action}
      title={
        withBasketSize ? (
          <FormattedMessage id="campaigns.form.rules.title.audience-and-size" />
        ) : (
          <FormattedMessage id="campaigns.form.rules.title.audience" />
        )
      }
      value={
        <>
          {withBasketSize && (
            <CampaignRulesViewText>
              {minBasketSize === 0 ? (
                <FormattedMessage id="campaigns.form.rules.minimum-basket-size" />
              ) : (
                <FormattedMessage
                  id="campaigns.form.rules.placeholder"
                  values={{ amount: <MonetaryAmount amount={minBasketSize} currency={currency} /> }}
                />
              )}
            </CampaignRulesViewText>
          )}
          <CampaignRulesViewText>
            <FormattedMessage
              id="campaigns.form.audience.placeholder"
              values={{
                audience: firstPurchaseOnly ? (
                  <FormattedMessage id="campaigns.form.audience.placeholder-first-time" />
                ) : (
                  <FormattedMessage id="campaigns.form.audience.placeholder-everybody" />
                ),
              }}
            />
          </CampaignRulesViewText>
          {!firstPurchaseOnly && (
            <CampaignRulesViewText>
              {usageType === 'indefinitely' ? (
                <FormattedMessage id="campaigns.form.user-limit.placeholder" />
              ) : (
                <FormattedMessage
                  id="campaigns.form.user-limit.placeholder-limit"
                  values={{
                    countTime: (
                      <FormattedPluralMessage id="campaigns.form.user-limit.times" count={perUserUsageLimit || 0} />
                    ),
                  }}
                />
              )}
            </CampaignRulesViewText>
          )}
        </>
      }
    />
  );
};

export default RulesDetails;
